import { FC, useMemo } from "react";
import OrderOptimizerRailyDetailsWithoutDriverTableRow from "../common/types/order-optimizer-raily-details-without-driver-table-row";
import orderOptimizerRailyDetailsWithoutDriverTableHelper from "./order-optimizer-raily-details-without-driver-table.helper";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import uuidService from "../../../../../common/utils/uuid/uuid.service";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import {
  OrderOptimizerRailyDetailsItemOptimizedStats,
  OrderOptimizerRailyDetailsItemUnoptimizedStats,
} from "../common/types/order-optimizer-raily-details-item";
import orderOptimizerRailyDetailsHelper from "../order-optimizer-raily-details.helper";

type OrderOptimizerRailyDetailsWithoutDriverTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsOptimizedStatsItem?: OrderOptimizerRailyDetailsItemOptimizedStats;
  detailsUnoptimizedStatsItem?: OrderOptimizerRailyDetailsItemUnoptimizedStats;
};

const OrderOptimizerRailyDetailsWithoutDriverTableComponent: FC<
  OrderOptimizerRailyDetailsWithoutDriverTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerRailyDetailsWithoutDriverTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createOptimizedStatsTableRow = (
    detailsOptimizedStatsItem: OrderOptimizerRailyDetailsItemOptimizedStats,
  ): OrderOptimizerRailyDetailsWithoutDriverTableRow => {
    const distanceLabel = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsOptimizedStatsItem.distance,
    );
    const distanceTitle = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsOptimizedStatsItem.distance,
    );

    const contractRateLabel = orderOptimizerRailyDetailsHelper.getRateLabel(
      detailsOptimizedStatsItem.contractRate,
    );
    const contractRateTitle = orderOptimizerRailyDetailsHelper.getRateLabel(
      detailsOptimizedStatsItem.contractRate,
    );

    const totalTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.totalTime,
    );
    const totalTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.totalTime,
    );

    const haltingTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.haltingTime,
    );

    const marginLabel = orderOptimizerRailyDetailsHelper.getMarginLabel(
      detailsOptimizedStatsItem.margin,
    );
    const marginTitle = orderOptimizerRailyDetailsHelper.getMarginLabel(
      detailsOptimizedStatsItem.margin,
    );

    const translations =
      orderOptimizerTranslationsHelper.getRailyDetailsTranslations().table
        .withoutDriver;

    return {
      id: uuidService.generate(),
      value: {
        withoutDriver: (
          <div title={translations.routeTitle}>{translations.routeLabel}</div>
        ),
        billingModel: <div title="ABA">ABA</div>,
        contractRate: <div title={contractRateTitle}>{contractRateLabel}</div>,
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={
              detailsOptimizedStatsItem.haltingTime < 0 ? "time_delayed" : ""
            }
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: <div title={marginTitle}>{marginLabel}</div>,
      },
    };
  };

  const createUnoptimizedStatsTableRow = (
    detailsUnoptimizedStatsItem: OrderOptimizerRailyDetailsItemUnoptimizedStats,
  ): OrderOptimizerRailyDetailsWithoutDriverTableRow => {
    const distanceLabel = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsUnoptimizedStatsItem.distance,
    );
    const distanceTitle = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsUnoptimizedStatsItem.distance,
    );

    const totalTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.totalTime,
    );
    const totalTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.totalTime,
    );

    const haltingTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.haltingTime,
    );

    const contractRateLabel = orderOptimizerRailyDetailsHelper.getRateLabel(
      detailsUnoptimizedStatsItem.contractRate,
    );
    const contractRateTitle = orderOptimizerRailyDetailsHelper.getRateLabel(
      detailsUnoptimizedStatsItem.contractRate,
    );

    const marginLabel = orderOptimizerRailyDetailsHelper.getMarginLabel(
      detailsUnoptimizedStatsItem.margin,
    );
    const marginTitle = orderOptimizerRailyDetailsHelper.getMarginLabel(
      detailsUnoptimizedStatsItem.margin,
    );

    const translations =
      orderOptimizerTranslationsHelper.getRailyDetailsTranslations().table
        .withoutDriver;

    return {
      id: uuidService.generate(),
      value: {
        withoutDriver: (
          <div title={translations.sumOfOrdersTitle}>
            {translations.sumOfOrdersLabel}
          </div>
        ),
        billingModel: <div title="ABA">ABA</div>,
        contractRate: <div title={contractRateTitle}>{contractRateLabel}</div>,
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={
              detailsUnoptimizedStatsItem.haltingTime < 0 ? "time_delayed" : ""
            }
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: <div title={marginTitle}>{marginLabel}</div>,
      },
    };
  };

  const rows: OrderOptimizerRailyDetailsWithoutDriverTableRow[] =
    useMemo(() => {
      if (
        !props.detailsOptimizedStatsItem ||
        !props.detailsUnoptimizedStatsItem
      )
        return [];

      const optimizedRows = createOptimizedStatsTableRow(
        props.detailsOptimizedStatsItem,
      );

      const unoptimizedRows = createUnoptimizedStatsTableRow(
        props.detailsUnoptimizedStatsItem,
      );

      return [unoptimizedRows, optimizedRows];
    }, [
      props.detailsUnoptimizedStatsItem,
      props.detailsOptimizedStatsItem,
      selectedAppLanguage,
    ]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerRailyDetailsWithoutDriverTableComponent;
